import { ParentContext } from 'global/cnr/contexts/ParentContext';
import { useCallback, useContext, useEffect, useState } from 'react';
import { sportsShowTypes } from '../cnr/reducers/SportsSeasonReducer';
import { _allowSportsUpdates, useCreateSports, useSeasonalData, useSeasonals } from './useSeasonals';
import { _matchSeasonalCollection } from './useSportRealtimeResults';
import { seasonalDataHelpers, seasonalUpdateTypes } from '../cnr/reducers/reducerHelpers/seasonalDataHelpers';
import { doc, setDoc, writeBatch } from 'firebase/firestore';
import { createRefPath_event } from 'global/firestoreData/appData/appRefPaths';
import { _cacheGroupTypes } from 'global/cnr/contexts/useHelpers/useTimestamp';

export const _seedSortTypes = {
  elo: 'elo',
  ew: 'ew',
  owp: 'owp',
  pd: 'pd',
  ppm: 'ppm',
  rpi: 'rpi',
  sos: 'sos',
  sr: 'sr',
}

export const usePlayoffInfo = ({ sportsKey, _latestSeason, sportPermissions, _sportsYearAbr } = {}) => {

  const parentContext = useContext(ParentContext);
  const { states, fns } = parentContext ?? {}
  const { app_state, paps_state } = states ?? {}
  const { mainDb } = app_state ?? {}
  const { database_fns } = fns ?? {}
  const { pathViews } = paps_state ?? {}

  const { updateSeasonalMatches_byLevel } = useSeasonalData()
  const { getFullPlayoffInfo, get_selectedPlayoffInfo } = useCreateSports()
  const { getPks } = useSeasonals();
  const { update_seasonalOrGlobal, getUpdateProps, getSports_collectionItem } = seasonalDataHelpers(mainDb, database_fns, pathViews)

  const [updating, setUpdating] = useState()
  const [confirmation, setConfirmation] = useState()
  const [sportsYearCollectionItem, setSportsYearCollectionItem] = useState()

  useEffect(() => {
    if (!confirmation) return

    const timer = setTimeout(() => {
      setConfirmation(false)
      setUpdating(false)
    }, 2500)

    return () => clearTimeout(timer)
  }, [confirmation])

  const createTeamsWithSeeds = useCallback(({ teams }) => {
    return createTeams_seeded(teams)
  }, [])

  const createCsv = useCallback(({ matches }) => {
    return buildPlayoffBracketCSV(matches)
  }, [])

  const sortTeamSeeds = useCallback(({ levelTeams, levelKey, sortBy }) => {
    const _playoffGroup = levelTeams[levelKey]
    const filteredTeams = _.filter(_playoffGroup, team => team?.record?.mp !== 0);
    // return sortTeams(filteredTeams, sortBy)
    let _teams_sorted;
    switch (sortBy) {
      case _seedSortTypes.ppm:
        _teams_sorted = _.orderBy(filteredTeams, ['record.ppm', 'record.owp', 'record.swp'], ['desc', 'desc', 'desc']);
        break;
      case _seedSortTypes.owp:
        _teams_sorted = _.orderBy(filteredTeams, ['record.owp', 'record.swp'], ['desc', 'desc']);
        break;
      case _seedSortTypes.elo:
        _teams_sorted = _.orderBy(filteredTeams, ['record.eloInfo.elo', 'record.owp', 'record.swp'], ['desc', 'desc', 'desc']);
        break;
      case _seedSortTypes.rpi:
        _teams_sorted = _.orderBy(filteredTeams, ['record.rpiInfo.rpi', 'record.owp', 'record.swp'], ['desc', 'desc', 'desc']);
        break;
      case _seedSortTypes.sr:
        _teams_sorted = _.orderBy(filteredTeams, ['record.sectionRank', 'record.owp', 'record.swp'], ['asc', 'desc', 'desc']);
        break;
      case _seedSortTypes.sos:
        _teams_sorted = _.orderBy(filteredTeams, ['record.eloInfo.strengthOfSchedule', 'record.owp', 'record.swp'], ['desc', 'desc', 'desc']);
        break;
      case _seedSortTypes.pd:
        _teams_sorted = _.orderBy(filteredTeams, ['record.eloInfo.performanceDelta', 'record.owp', 'record.swp'], ['desc', 'desc', 'desc']);
        break;
      default:
        _teams_sorted = _.orderBy(filteredTeams, ['record.owp', 'record.swp'], ['desc', 'desc']);
    }
    _.forEach(_teams_sorted, (t, index) => {
      t.seed = index + 1
      t.position = index
    })
    return _teams_sorted
  }, [])

  const sortPlayoffSeeds = useCallback(({ levelTeams, levelKey, sortBy }) => {
    const _playoffGroup = levelTeams[levelKey]
    const filteredTeams = _.filter(_playoffGroup, team => team?.record?.mp !== 0);
    // return sortTeams(filteredTeams, sortBy) 
    let _teams_sorted;
    switch (sortBy) {
      case _seedSortTypes.ppm:
        _teams_sorted = _.orderBy(filteredTeams, ['ppm', 'owp', 'swp'], ['desc', 'desc', 'desc']);
        break;
      case _seedSortTypes.owp:
        _teams_sorted = _.orderBy(filteredTeams, ['owp', 'swp'], ['desc', 'desc']);
        break;
      case _seedSortTypes.elo:
        _teams_sorted = _.orderBy(filteredTeams, ['eloInfo.elo', 'owp', 'swp'], ['desc', 'desc', 'desc']);
        break;
      case _seedSortTypes.rpi:
        _teams_sorted = _.orderBy(filteredTeams, ['rpiInfo.rpi', 'owp', 'swp'], ['desc', 'desc', 'desc']);
        break;
      case _seedSortTypes.sr:
        _teams_sorted = _.orderBy(filteredTeams, ['sectionRank', 'owp', 'swp'], ['asc', 'desc', 'desc']);
        break;
      case _seedSortTypes.sos:
        _teams_sorted = _.orderBy(filteredTeams, ['eloInfo.strengthOfSchedule', 'owp', 'swp'], ['desc', 'desc', 'desc']);
        break;
      case _seedSortTypes.pd:
        _teams_sorted = _.orderBy(filteredTeams, ['eloInfo.performanceDelta', 'owp', 'swp'], ['desc', 'desc', 'desc']);
        break;
      default:
        _teams_sorted = _.orderBy(filteredTeams, ['eloInfo.elo', 'owp', 'swp'], ['desc', 'desc', 'desc']);
      // _teams_sorted = _.orderBy(filteredTeams, ['owp', 'swp'], ['desc', 'desc']);
    }
    _.forEach(_teams_sorted, (t, index) => {
      t.seed = index + 1
      t.position = index
    })
    return _teams_sorted
  }, [])

  const updateSeasonalPlayoffInfo = useCallback(({ createPlayoff_state }) => {
    const cb = (res) => setUpdating(false)
    setUpdating(true)
    const _playoffInfos = get_selectedPlayoffInfo(createPlayoff_state, _latestSeason)
    // updates the `_playoffs` collection with the document matching the parentKeys
    // _playoffs/playoffInfo  
    const _pkss = getPks(pathViews, _latestSeason, sportsKey)
    const _updateProps = getUpdateProps(sportPermissions, _pkss, cb, 'playoffs', null, null, null, _playoffInfos, _sportsYearAbr)
    update_seasonalOrGlobal(seasonalUpdateTypes.playoffInfo, _updateProps)
  }, [])

  /**
   * This is the function that will be called when the user clicks the "Save Playoff Tournament" button. It will take the playoff match info and update the seasonal matches collection with the new playoff matches. If "createAllPlayoffMatches" mode is selected, it will update all playoff levels. Otherwise, it will only update the selected playoff level.
   */
  const savePlayoffTournament = useCallback(async ({ playoff_matchInfo, playoffLevel, mode_right, cbAfter }) => {
    const cb = (confirmation) => {
      setConfirmation(confirmation)
      cbAfter && cbAfter()
    }
    let allPlayoffLevels = false;
    switch (mode_right) {
      case sportsShowTypes.createAllPlayoffMatches:
        allPlayoffLevels = true
        break;
      default:
      // nothing
    }
    // createPlayoff_handlers.handleStartUpdate()
    setUpdating(true)
    const _playoff_matchInfo = _.clone(playoff_matchInfo)
    const playoffMatches = getFullPlayoffInfo(_playoff_matchInfo, playoffLevel, allPlayoffLevels)
    const _pks = getPks(pathViews, _latestSeason, sportsKey)
    const _collection = `_${_matchSeasonalCollection}`
    const _sportsYearCollectionItem = await getSports_collectionItem(_pks, _collection, true)
    const { _itemKey } = _sportsYearCollectionItem ?? {}
    if (_itemKey) {
      const refPath = createRefPath_event(pathViews, [_collection, _itemKey, 'playoffMatches', playoffLevel])
      await updatePlayoffLevelMatches({ mainDb, database_fns, refPath, pathViews, fieldName: 'playoffMatches', merge: true, fieldData: playoffMatches, playoffLevel, callback: cb })
    } else {
      console.group('updateDocField')
      console.log('No sportsYearCollectionItem', _pks)
      console.groupEnd()
      cb({ success: false, header: 'No sportsYearCollectionItem', content: 'No sportsYearCollectionItem' })
    }
  }, [])

  const getSportsYearCollectionItem = useCallback(async () => {
    const _pks = getPks(pathViews, _latestSeason, sportsKey)
    const _collection = `_${_matchSeasonalCollection}`
    const _sportsYearCollectionItem = await getSports_collectionItem(_pks, _collection, true)
    setSportsYearCollectionItem(_sportsYearCollectionItem)
  }, [])



  return {
    sortTeamSeeds,
    getSportsYearCollectionItem,
    sortPlayoffSeeds,
    createTeamsWithSeeds,
    updateSeasonalPlayoffInfo,
    savePlayoffTournament,
    createCsv,
    sportsYearCollectionItem,
    updating,
    confirmation
  }
}

const buildPlayoffBracketCSV = (matches = {}) => {

  const getMatch = (homeSeed, awaySeed) => {
    return _.find(matches, m =>
      _.get(m, 'seeds.home') === homeSeed &&
      _.get(m, 'seeds.away') === awaySeed
    )
  }

  const format = (m) => {
    if (!m) return ''

    const h = `${m.seeds.home} ${m.home.name}`
    const a = `${m.seeds.away} ${m.away.name}`
    return `${h} vs ${a}`
  }

  // 8-row bracket grid (top to bottom spacing matters visually)
  const grid = [
    [format(getMatch(1, 16)), '', '', format(getMatch(2, 15))],
    ['', '', '', ''],
    [format(getMatch(8, 9)), '', '', format(getMatch(7, 10))],
    ['', '', '', ''],
    [format(getMatch(5, 12)), '', '', format(getMatch(6, 11))],
    ['', '', '', ''],
    [format(getMatch(4, 13)), '', '', format(getMatch(3, 14))],
  ]

  return grid
    .map(row => row.map(v => v || '').join(','))
    .join('\n')

}

const createTeams_seeded = (teams) => {
  const _seededTeams = {}
  _.forEach(teams, (team, index) => {
    const { id, name, levels, sections, record } = team
    const { sectionRank } = record ?? {}
    _seededTeams[id] = {
      id: id,
      name: name,
      levels: levels,
      sections: sections,
      record: {
        ...record,
        sectionRank,
      },
      seed: index,
      position: index,
    }
  })
  return _seededTeams
}

const updatePlayoffLevelMatches = async ({
  mainDb,
  database_fns,
  refPath,
  fieldName,
  fieldData = {},
  merge,
  playoffLevel,
  pathViews,
  callback
}) => {

  const batch = writeBatch(mainDb)

  const pathParts = refPath.split('/').filter(Boolean)

  if (!_.isArray(pathParts) || pathParts.length % 2 !== 0) {
    console.group('updateDocField')
    console.log('Invalid Firestore path', pathParts)
    console.groupEnd()
    callback({ success: false, header: 'Invalid Firestore path', content: pathParts })
  }

  const dataToUpdate = {
    [fieldName]: fieldData
  }

  const ref = doc(mainDb, refPath)

  console.group('updateDocField')
  console.log('ref', ref)
  console.log('refPath', refPath)
  console.log('dataToUpdate', dataToUpdate)
  console.groupEnd()

  if (_allowSportsUpdates.playoffMatches) {
    // await setDoc(ref, dataToUpdate, { merge: merge })
    batch.update(ref, dataToUpdate)
    const _timestamp = Date.now()
    database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonResultsInfo, pathViews.sports, _timestamp, batch)
    batch.commit();
  }

  const _updateCaption = _allowSportsUpdates.playoffMatches ? 'Updated' : 'would have been Updated'
  callback({ success: true, content: `${_.size(fieldData)} Playoff Matches ${_updateCaption} for ${playoffLevel}`, header: 'Playoff Matches Updated' })

}